import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Gamepad2, Plus, ArrowRight, Ticket, Monitor } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';

export default function ClubBingoAdminPage() {
  const { clubSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [club, setClub] = useState(null);
  const [onlineGames, setOnlineGames] = useState([]);
  const [paperGames, setPaperGames] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClubAndGames = async () => {
      try {
        // Fetch club info
        const clubRes = await fetch(`${API_URL}/api/clubs/${clubSlug}`);
        if (!clubRes.ok) {
          setError('Club not found');
          setLoading(false);
          return;
        }
        const clubData = await clubRes.json();
        setClub(clubData);

        // Fetch BOTH types of active bingo games in parallel
        const [onlineRes, paperRes] = await Promise.all([
          fetch(`${API_URL}/api/bingo/clubs/${clubData.club_id}/games`),
          fetch(`${API_URL}/api/bingo/paper/club/${clubData.club_id}/active`)
        ]);
        
        // Online bingo games
        if (onlineRes.ok) {
          const onlineData = await onlineRes.json();
          // Filter to only active/waiting games
          const activeOnline = (onlineData.games || []).filter(g => 
            g.status === 'waiting' || g.status === 'active' || g.status === 'paused'
          );
          setOnlineGames(activeOnline);
        }
        
        // Paper bingo games
        if (paperRes.ok) {
          const paperData = await paperRes.json();
          setPaperGames(paperData.games || []);
        }
        
      } catch (err) {
        console.error('Error:', err);
        setError('Failed to load club data');
      } finally {
        setLoading(false);
      }
    };

    fetchClubAndGames();
  }, [clubSlug]);

  const handleCreateOnlineGame = () => {
    navigate('/bingo', { state: { clubId: club?.club_id, clubName: club?.name } });
  };
  
  const handleCreatePaperGame = () => {
    navigate('/bingo/paper', { state: { clubId: club?.club_id, clubName: club?.name } });
  };

  const handleResumeOnlineGame = (gameCode) => {
    navigate(`/bingo/host/${gameCode}`);
  };
  
  const handleResumePaperGame = (gameCode) => {
    navigate(`/bingo/paper/${gameCode}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading bingo admin...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6 text-center">
            <Gamepad2 className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Error</h2>
            <p className="text-slate-400">{error}</p>
            <Button 
              onClick={() => navigate('/dashboard')} 
              className="mt-4"
            >
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasActiveGames = onlineGames.length > 0 || paperGames.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Gamepad2 className="w-16 h-16 text-purple-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">
            {club?.name} - Bingo Admin
          </h1>
          <p className="text-slate-400">
            Manage your Line Dance Bingo games
          </p>
        </div>

        {/* Online Bingo Games */}
        {onlineGames.length > 0 && (
          <div className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Monitor className="w-5 h-5 text-green-400" />
              Online Bingo Games
            </h2>
            {onlineGames.map((game) => (
              <Card 
                key={game.game_code} 
                className="bg-slate-800/50 border-slate-700 hover:border-green-500/50 transition-colors cursor-pointer"
                onClick={() => handleResumeOnlineGame(game.game_code)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-white">{game.game_name || 'Bingo Game'}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          game.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          game.status === 'paused' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {game.status === 'waiting' ? 'Waiting for players' : game.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400">
                        Code: <span className="font-mono text-green-400">{game.game_code}</span>
                        {' • '}
                        {game.players?.length || 0} players
                        {' • '}
                        {game.called_dances?.length || 0}/90 called
                      </p>
                    </div>
                    <Button variant="outline" className="gap-2 border-green-500/50 text-green-400 hover:bg-green-500/10">
                      Resume <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Paper Bingo Games */}
        {paperGames.length > 0 && (
          <div className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Ticket className="w-5 h-5 text-purple-400" />
              Paper Bingo Games
            </h2>
            {paperGames.map((game) => (
              <Card 
                key={game.game_code} 
                className="bg-slate-800/50 border-slate-700 hover:border-purple-500/50 transition-colors cursor-pointer"
                onClick={() => handleResumePaperGame(game.game_code)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-white">{game.game_name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          game.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {game.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400">
                        Code: <span className="font-mono text-purple-400">{game.game_code}</span>
                        {' • '}
                        {game.tickets?.length || 0} tickets
                        {' • '}
                        {game.called_dances?.length || 0}/90 called
                      </p>
                    </div>
                    <Button variant="outline" className="gap-2 border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
                      Resume <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No Active Games */}
        {!hasActiveGames && (
          <Card className="bg-slate-800/50 border-slate-700 mb-8">
            <CardContent className="p-8 text-center">
              <Gamepad2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No Active Games</h3>
              <p className="text-slate-400 mb-4">
                You don't have any active bingo games. Start a new one!
              </p>
            </CardContent>
          </Card>
        )}

        {/* Create New Game Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            onClick={handleCreateOnlineGame}
            size="lg"
            className="gap-2 bg-green-600 hover:bg-green-700"
          >
            <Monitor className="w-5 h-5" />
            New Online Bingo
          </Button>
          <Button 
            onClick={handleCreatePaperGame}
            size="lg"
            className="gap-2 bg-purple-600 hover:bg-purple-700"
          >
            <Ticket className="w-5 h-5" />
            New Paper Bingo
          </Button>
        </div>

        {/* Bookmark Hint */}
        <div className="mt-8 p-4 bg-slate-800/30 rounded-xl border border-slate-700/50 text-center">
          <p className="text-sm text-slate-400">
            Bookmark this page to quickly access your bingo admin anytime!
          </p>
          <p className="text-xs text-slate-500 mt-1 font-mono">
            {window.location.origin}/club/{clubSlug}/bingo/admin
          </p>
        </div>
      </div>
    </div>
  );
}
